import React from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { PAPERS_INTERVENCIO, MONEDES, potJutjarFactibilitat } from '../../analisisSSM';

// LES TRES ANÀLISIS DE CHECKLAND, dins de l'etapa 1.
//
// Les regles i el que n'arriba a l'etapa 6 són a `src/analisisSSM.js`, amb proves pròpies
// (`npm run prova:analisis`). Aquí només hi ha els camps.
//
// Van dins de l'etapa 1 i no a una etapa pròpia perquè **són el «finding out»**: no són un pas
// més de la seqüència, són tres maneres de mirar-se la mateixa situació mentre s'explora.
//
// I hi ha un motiu pràctic: **l'etapa 6 té dos eixos** i el de «culturalment factible» no tenia
// cap font. Sense les Anàlisis 2 i 3, aquell eix és una casella on escriure el que un s'imagini.

const Llista = ({ valors, onCanvi, marcador }) => {
  const { t } = useTranslation();
  const [nou, setNou] = React.useState('');
  const afegeix = () => {
    if (!nou.trim()) return;
    onCanvi([...(valors || []), nou.trim()]);
    setNou('');
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {(valors || []).map((v, i) => (
          <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-md
                                   bg-gray-100 text-xs text-gray-700">
            {v}
            <button type="button" onClick={() => onCanvi(valors.filter((_, j) => j !== i))}
              className="text-gray-400 hover:text-gray-600" aria-label={t('common.remove')}>
              <XMarkIcon className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-1.5">
        <input value={nou} onChange={(e) => setNou(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); afegeix(); } }}
          placeholder={marcador}
          className="flex-1 px-2 py-1.5 border border-gray-300 rounded-md text-sm
                     focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
        <button type="button" onClick={afegeix}
          className="px-2 py-1.5 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50">
          <PlusIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const Taula = ({ files, columnes, onCanvi, afegirEtiqueta }) => {
  const { t } = useTranslation();
  const posa = (id, camp, valor) =>
    onCanvi((files || []).map((f) => (f.id === id ? { ...f, [camp]: valor } : f)));
  return (
    <div className="space-y-2">
      {(files || []).map((f) => (
        <div key={f.id} className="grid gap-2 items-start"
             style={{ gridTemplateColumns: `repeat(${columnes.length}, 1fr) auto` }}>
          {columnes.map(([camp, ph]) => (
            <input key={camp} value={f[camp] || ''}
              onChange={(e) => posa(f.id, camp, e.target.value)}
              placeholder={t(`ssm.${ph}`)}
              className="px-2 py-1.5 border border-gray-300 rounded-md text-sm
                         focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
          ))}
          <button type="button" onClick={() => onCanvi(files.filter((x) => x.id !== f.id))}
            className="p-1.5 text-gray-400 hover:text-gray-600" aria-label={t('common.remove')}>
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button type="button"
        onClick={() => onCanvi([...(files || []), { id: `f_${Date.now()}` }])}
        className="flex items-center gap-1.5 text-xs font-medium text-primary-700 hover:underline">
        <PlusIcon className="h-3.5 w-3.5" />{afegirEtiqueta}
      </button>
    </div>
  );
};

const Seccio = ({ titol, subtitol, avis, children }) => (
  <section className="bg-white border border-gray-200 rounded-md p-5">
    <h3 className="text-base font-semibold text-gray-900">{titol}</h3>
    <p className="text-sm text-gray-600 mt-1 mb-4">{subtitol}</p>
    {avis && (
      <p className="text-xs text-gray-700 bg-primary-50 border border-primary-200 rounded-md p-2.5 mb-4">
        {avis}
      </p>
    )}
    {children}
  </section>
);

export default function TresAnalisis({ problemSituation, onCanvi }) {
  const { t } = useTranslation();
  const p = problemSituation || {};
  const u = p.analysisOne || { client: [], practitioner: [], issueOwners: [] };

  const posaU = (camp, valors) => onCanvi({ analysisOne: { ...u, [camp]: valors } });

  return (
    <div className="space-y-5">
      <Seccio titol={t('ssm.an1')} subtitol={t('ssm.an1d')} avis={t('ssm.an1Avis')}>
        <div className="grid md:grid-cols-3 gap-4">
          {PAPERS_INTERVENCIO.map((camp) => (
            <div key={camp}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t(`ssm.an1_${camp}`)}
              </label>
              <p className="text-xs text-gray-500 mb-2">{t(`ssm.an1_${camp}d`)}</p>
              <Llista valors={u[camp]} onCanvi={(v) => posaU(camp, v)}
                marcador={t(`ssm.an1_${camp}Ph`)} />
            </div>
          ))}
        </div>
      </Seccio>

      <Seccio titol={t('ssm.an2')} subtitol={t('ssm.an2d')}>
        <div className="grid gap-2 mb-2 text-xs font-medium text-gray-500"
             style={{ gridTemplateColumns: 'repeat(3, 1fr) auto' }}>
          <span>{t('ssm.an2Paper')}</span><span>{t('ssm.an2Normes')}</span>
          <span>{t('ssm.an2Valors')}</span><span className="w-7" />
        </div>
        <Taula files={p.analysisTwo} onCanvi={(v) => onCanvi({ analysisTwo: v })}
          columnes={[['role', 'an2PaperPh'], ['norms', 'an2NormesPh'], ['values', 'an2ValorsPh']]}
          afegirEtiqueta={t('ssm.an2Afegir')} />
      </Seccio>

      <Seccio titol={t('ssm.an3')} subtitol={t('ssm.an3d')} avis={t('ssm.an3Avis')}>
        {/* Els exemples no són cap plantilla per omplir: hi són per no quedar-se només amb
            l'autoritat formal, que és la que es veu i gairebé mai la que decideix. */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {MONEDES.map((m) => (
            <button key={m} type="button"
              onClick={() => onCanvi({ analysisThree: [...(p.analysisThree || []),
                { id: `f_${Date.now()}_${m}`, commodity: t(`ssm.an3_${m}`), how: '' }] })}
              className="px-2 py-1 rounded-md border border-dashed border-gray-300 text-xs
                         text-gray-600 hover:bg-gray-50">
              + {t(`ssm.an3_${m}`)}
            </button>
          ))}
        </div>
        <div className="grid gap-2 mb-2 text-xs font-medium text-gray-500"
             style={{ gridTemplateColumns: 'repeat(2, 1fr) auto' }}>
          <span>{t('ssm.an3Moneda')}</span><span>{t('ssm.an3Com')}</span><span className="w-7" />
        </div>
        <Taula files={p.analysisThree} onCanvi={(v) => onCanvi({ analysisThree: v })}
          columnes={[['commodity', 'an3MonedaPh'], ['how', 'an3ComPh']]}
          afegirEtiqueta={t('ssm.an3Afegir')} />
      </Seccio>

      {/* Es diu si l'etapa 6 tindrà amb què jutjar la factibilitat. Es diu i no s'impedeix: es
          pot arribar a l'etapa 6 sense les Anàlisis, però qui hi arribi ha de saber que llavors
          se les inventarà. */}
      <p className={`text-xs px-3 py-2 rounded-md border ${potJutjarFactibilitat(p)
        ? 'text-gray-600 bg-gray-50 border-gray-200'
        : 'text-gray-700 bg-white border-warn-400'}`}>
        {potJutjarFactibilitat(p) ? t('ssm.anLlest') : t('ssm.anFalta')}
      </p>
    </div>
  );
}
